import Decimal from 'decimal.js';
import has from 'lodash/has';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';

/**
 * Rounds a number up to the specified number of decimal places.
 * @param {Number|String|Object} value
 * @param {Number} decimals
 * @returns {Number}
 */
export const ceil = (value = 0, decimals = 2) => {
    let decimalValue = new Decimal(value);
    let truncatedValue = decimalValue.toDecimalPlaces(decimals, Decimal.ROUND_UP);

    return parseFloat(truncatedValue.toString());
};

/**
 * Rounds a number down to the specified number of decimal places, avoiding precision issues.
 * @param {Number|String|Object} value
 * @param {Number} decimals
 * @returns {Number}
 */
export const floor = (value = 0, decimals = 2, fallback = 0) => {
    try {
        if (value === null || value === Infinity || value === -Infinity) {
            return fallback;
        }

        let numericValue = value;

        if (typeof numericValue === 'string') {
            numericValue = parseFloat(numericValue);
        } else if (typeof numericValue !== 'number') {
            numericValue = Number(numericValue);
        }

        if (!Number.isFinite(numericValue)) {
            return fallback;
        }

        const decimalValue = new Decimal(numericValue);
        const truncatedValue = decimalValue.toDecimalPlaces(decimals, Decimal.ROUND_DOWN);

        const result = parseFloat(truncatedValue.toString());

        return Number.isFinite(result) ? result : fallback;
    } catch (error) {
        return fallback;
    }
};

/**
 * Check if a string is numeric
 * @param {String} str
 * @returns {Boolean} true if str is numeric
 */
export const isNumeric = str => {
    if (typeof str != 'string') return false;
    return !isNaN(str) && !isNaN(parseFloat(str));
};

/**
 * Percentage Formatting
 * @param {Number} value
 * @returns String
 */
export const toPercentage = (value, decimals = 2, abbreviate = false) => {
    let options = {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: isObject(decimals) ? 2 : decimals
    };

    if (isObject(decimals)) {
        options = {
            ...options,
            ...decimals
        };
    }

    if (abbreviate) {
        options = {
            ...options,
            notation: 'compact',
            compactDisplay: 'short'
        };
    }

    return toNumber(value, options);
};

/**
 * Number Formatting
 * @param {Number} value
 * @returns String
 */
const COMPACT_SUFFIX_PATTERN = /(\d)([kmbt])(?![a-z])/gi;

const withUppercaseCompactSuffix = formatted =>
    formatted.replace(COMPACT_SUFFIX_PATTERN, (_, digit, unit) => `${digit}${unit.toUpperCase()}`);

export const toNumber = (value, options = {}, returnObject = null) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (!isNumber(value)) {
        return null;
    }

    options = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        ...options
    };

    const formatter = new Intl.NumberFormat('en-GB', options);

    if (returnObject === null) {
        const formatted = formatter.format(value);

        if (options.notation === 'compact') {
            return withUppercaseCompactSuffix(formatted);
        }

        return formatted;
    }

    return formatter;
};

/**
 * Currency Formatting
 * @param {Number} value
 * @returns String
 */
export const toCurrency = (value, currency = 'GBP', decimals = 2, abbreviate = false) => {
    let options = {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: isObject(decimals) ? 2 : decimals
    };

    if (isObject(decimals)) {
        options = {
            ...options,
            ...decimals
        };
    }

    if (abbreviate) {
        options = {
            ...options,
            notation: 'compact',
            compactDisplay: 'short',
            minimumFractionDigits: 0
        };
    }

    return toNumber(value, options);
};

export const getRandomInt = (min = 0, max = 100) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Return the formatting options for currency
 * @param {Object|String|Number} v
 */
export const getCurrencyFormatOptions = v => {
    if (isObject(v) && has(v, 'amount')) {
        v = v.amount;
    }

    let isWholeNumber = v % 1 === 0;

    let decimals = 0;
    let abbreviate = false;
    let minimumFractionDigits = 2;
    let maximumFractionDigits = 2;

    if (v > 999999) {
        decimals = 1;
    }

    if (v > 99999) {
        abbreviate = true;
        minimumFractionDigits = 0;
        maximumFractionDigits = 1;
    }

    if (!abbreviate && isWholeNumber) {
        decimals = 0;
        minimumFractionDigits = 0;
        maximumFractionDigits = 0;
    }

    return { decimals, abbreviate, minimumFractionDigits, maximumFractionDigits };
};
